import { CircularProgress } from '@mui/material';
import classnames from 'classnames';
import FormatCardContainer from 'js/components/destination/destination-content/FormatCardNew';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import MentionsBasicEditor from 'js/components/elements/editors/draftjs/MentionsBasicEditor';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';
import Constants, { CONNECTORS_WITHOUT_PATH } from 'js/Constants';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  ID_BASED_FILE_CONNECTORS,
  ID_BASED_FILE_CONNECTORS_WITH_LEAF_ID_ONLY,
} from 'js/constants/connectionCategories';
import {
  SUPPORT_KEY_SINK_FORM_FILE,
  SUPPORT_KEY_SOURCE_FORM_FILE,
} from 'js/constants/supportPageKeys';
import {
  convertTextToMentionsState,
  DEFAULT_MACRO_ENTITY_NAME,
  extractPayloadFromMentionContent,
} from 'js/lib/DraftJSUtils';
import JSONUtils from 'js/lib/JSONUtils';
import { getCurrentOrg } from 'js/reducers/session';
import { filter, groupBy } from 'lodash';
import { ReactComponent as RefreshIconRotate } from 'new/components/icons/fi-rr-refresh-rotate.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { Box } from 'new/elements/Box';
import { EmptyView } from 'new/elements/EmptyView';
import { ExpandableSearchBar } from 'new/elements/ExpandableSearchBar';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { SearchableProvider } from 'new/elements/SearchableProvider';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/TypographySupport';
import {
  getFeatureFlagValue,
  isSourceFormRuntimeSelectionEnabled,
} from 'new/constants/featureFlags';
import { tryCatchAsync } from 'new/utils/TryCatch';
import React from 'react';
import { connect } from 'react-redux';
import { ResourcePopoverMenu } from 'src/new/elements/ResourcePopoverMenu';
import UIUtils from '../../../lib/UIUtils';
import FilePreview from '../../elements/FilePreview/FilePreview';
import Tree from '../../elements/treeview/Tree';
import AdvancedConfiguration from './AdvancedConfiguration';
import DataSourceDetails from './DataSourceDetails/DataSourceDetails';
import MonitoringInterval from './MonitoringInterval/MonitoringInterval';
import SelectBucketTreeViewNode from './SelectBucketTreeViewNode';
import SelectedLocationPathInput from './SelectedLocationPathInput/SelectedLocationPathInput';
import FileFilters from './FileFilters';
import { getFileIngestModifiedAfterMs } from './SelectBucket.utils';

import { BreadcrumbTreeView } from 'new/components/BreadcrumbTreeView';
import { SearchInjector } from 'new/elements/SearchableProvider/SearchInjector';
import { UiMode } from 'new/constants/uiMode';
import { withUiModeCode } from 'new/utils/uiMode';

import './SelectBucket.scss';

var _ = require('lodash');

const customPrefixSystemMacros = [
  {
    name: '{yyyy}',
    display_name: 'yyyy - 4 Digit Year',
    type: 'sys',
    type_display: 'System Attributes',
  },
  {
    name: '{MM}',
    display_name: 'MM - 2 Digit Month',
    type: 'sys',
    type_display: 'System Attributes',
  },
  {
    name: '{dd}',
    display_name: 'dd - date',
    type: 'sys',
    type_display: 'System Attributes',
  },
  {
    name: '{HH}',
    display_name: 'HH - Hour',
    type: 'sys',
    type_display: 'System Attributes',
  },
  {
    name: '{mm}',
    display_name: 'mm - Minute',
    type: 'sys',
    type_display: 'System Attributes',
  },
  {
    name: '{ss}',
    display_name: 'ss - Second',
    type: 'sys',
    type_display: 'System Attributes',
  },
];

const replaceMacroMap = {
  grouped: true,
  sys: {
    prefix: '{',
    suffix: '}',
  },
  attr: {
    prefix: '{record.',
    suffix: '}',
  },
  default: {
    prefix: '{record.',
    suffix: '}',
  },
};

const getMentionsFromSchema = (dataSchema) => {
  const attrsList = _.map(dataSchema, (attr) => {
    const attrName = UIUtils.getAttrDisplayName(attr);

    return {
      name: `{record.${attrName}}`,
      display_name: attrName,
      type: 'attr',
      type_display: 'Data Attributes',
    };
  });

  return _.concat(customPrefixSystemMacros, attrsList);
};

const getStateFromProps = (props) => {
  const { dataSchema, selectedBucket } = props;

  let customPrefix = getDefaultCustomPrefix(props);

  let allPrefixSuggestions = getMentionsFromSchema(dataSchema);
  let customPrefixEditorState;

  if (customPrefix) {
    customPrefixEditorState = convertTextToMentionsState(
      customPrefix,
      allPrefixSuggestions,
      DEFAULT_MACRO_ENTITY_NAME,
      replaceMacroMap,
    );
  } else {
    customPrefixEditorState = convertTextToMentionsState(customPrefix, null);
  }

  const selectedNodeId = selectedBucket && selectedBucket.path;

  return {
    hidePath: CONNECTORS_WITHOUT_PATH.includes(props.selectedConnection),
    customPrefixEditorState,
    allPrefixSuggestions,
    filteredPrefixSuggestions: allPrefixSuggestions,
    selectedNodeId,
    stateUpdatedAt: Date.now(),
  };
};

const getDefaultCustomPrefix = (props) => {
  const { sourceConfig, allowCustomPrefixEditor } = props;

  let customPrefix = null;
  if (allowCustomPrefixEditor) {
    const pollFrequency = sourceConfig.poll_frequency;

    //if prefix cutom part is already set, use it as default
    if (_.has(sourceConfig, 'output.dir.name.pattern')) {
      customPrefix = sourceConfig['output.dir.name.pattern'];
    } else if (pollFrequency === 'Hour' || pollFrequency === '"Minute"') {
      customPrefix = '{yyyy}/{MM}/{dd}/{HH}';
    } else {
      customPrefix = '{yyyy}/{MM}/{dd}';
    }
  }

  return customPrefix;
};

class SelectBucket extends React.Component {
  constructor(props) {
    super(props);

    this.state = getStateFromProps(props);

    // Track probe call count per path to enforce hard limit of 2 calls maximum
    this.probeCallCountMap = new Map();
    this.maxProbeCalls = 2;

    this._updateSourceConfigPrefix = this._updateSourceConfigPrefix.bind(this);
    this._handleNodeExpandClicked = this._handleNodeExpandClicked.bind(this);
    this._handleNodePreview = this._handleNodePreview.bind(this);
    this._handleNodeSelected = this._handleNodeSelected.bind(this);
    this._handleRefreshTreeClicked = this._handleRefreshTreeClicked.bind(this);
    this._handleIdPathResolved = this._handleIdPathResolved.bind(this);

    this._handlePrefixEditorChange = this._handlePrefixEditorChange.bind(this);
    this._handlePrefixEditorSearch = this._handlePrefixEditorSearch.bind(this);
    this._handlePrefixEditorKeyDown = this._handlePrefixEditorKeyDown.bind(this);
  }

  componentDidMount() {
    //Fetch probe tree for this data source
    const { sourceConfig, allowCustomPrefixEditor, selectedBucket, resourceMode } = this.props;

    // If we have a configured path (editing source or sink), skip root probe - BreadcrumbTreeView will probe the configured path directly.
    // This avoids the "No files or folders found" flash when the root probe completes before the configured path probe.
    // In create mode (no configured path), probe root to show initial folder list.
    const hasConfiguredPath = selectedBucket?.path;

    // DEBUG: Uncomment to debug double probe issues
    // console.log('[SelectBucket] componentDidMount:', {
    //   resourceMode,
    //   hasConfiguredPath,
    //   selectedBucket: selectedBucket ? { path: selectedBucket.path, bucket: selectedBucket.bucket } : null,
    //   willProbeRoot: !hasConfiguredPath
    // });

    if (!hasConfiguredPath) {
      this.onRequestTree();
    }

    if (allowCustomPrefixEditor && !_.has(sourceConfig, 'output.dir.name.pattern')) {
      this._updateSourceConfigPrefix();
    }

    if (this.props.setContextualSupport) {
      if (this.props.resourceMode !== 'source') {
        this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_FILE);
      } else {
        this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_FILE);
      }
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      if (this.props.resourceMode !== 'source') {
        this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_FILE);
      } else {
        this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_FILE);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { allowCustomPrefixEditor, selectedCredentialId, selectedBucket } = this.props;

    if (!prevProps.allowCustomPrefixEditor && allowCustomPrefixEditor === true) {
      this.setState(getStateFromProps(this.props), this._updateSourceConfigPrefix);

      /*
      //TODO:R16: this seems unncessary after removing cwrp
       if (!_.has(sourceConfig, 'output.dir.name.pattern')) {
        let customPrefixPart = this._saveCustomPrefix(
          this.state.customPrefixEditorState
        );
        let newSourceConfig = {
          ...sourceConfig,
          'output.dir.name.pattern': customPrefixPart
        };
        this.props.onSourceConfigChanged(newSourceConfig);
      }
      */
    }

    // Reset probe count when selectedBucket path changes
    if (prevProps.selectedBucket?.path !== selectedBucket?.path && selectedBucket?.path) {
      this.probeCallCountMap.delete(prevProps.selectedBucket?.path);
      this.probeCallCountMap.delete(selectedBucket.path);
    }

    // When credential ID changes, probe root for credential validation and to populate base tree
    // UNLESS we have a configured path (edit mode) - then BreadcrumbTreeView will probe the configured path directly
    if (prevProps.selectedCredentialId !== selectedCredentialId) {
      const hasConfiguredPath = selectedBucket?.path;

      // DEBUG: Uncomment to debug credential change probe behavior
      // console.log('[SelectBucket] componentDidUpdate - credential changed:', {
      //   hasConfiguredPath,
      //   willProbeRoot: !hasConfiguredPath
      // });

      if (!hasConfiguredPath) {
        this.props.onRequestProbeTree();
      }
    }
  }

  closeDialog = () => {
    this.setState({
      fileNode: null,
    });
  };

  onRequestTree = () => {
    if (this.props.selectedCredentialId && !this.state.hidePath) {
      this.props.onRequestProbeTree();
    }
  };

  render() {
    // In edit mode with a configured path, show BreadcrumbTreeView immediately
    // and let it handle loading for the specific path (similar to user typing a path)
    const isEditModeWithPath = this.props.sourceBeingEdited && this.props.selectedBucket?.path;

    // Only show loading spinner in create mode when we have no data yet
    // In edit mode, we skip the loading spinner and let BreadcrumbTreeView handle it
    const isLoading =
      !isEditModeWithPath &&
      _.isEmpty(this.props.probeData) &&
      this.props.isLoadingProbeData &&
      this.props.shouldShowLoading;
    let connectionCategory;
    const meta = this.props.connectionMetas[this.props.selectedConnection];
    if (meta) {
      connectionCategory = meta.connectionCategory;
    }
    /* TODO: This is not really working I thinkg we are moving away from fa, so this need to change */
    const iconClasses =
      connectionCategory === CONNECTION_CATEGORY_DATABASE
        ? {
            top: 'fa-table',
            nodes: 'fa-table',
            leaf: 'fa-minus',
          }
        : {
            top: 'fa-folder',
            root: 'fa-folder',
            leaf: 'fa-file',
          };

    let advancedFields = !!this.props.sourceConfig
      ? this.props.sourceConfigFields
      : this.props.sinkConfigFields;

    if (this.props.selectedBucket && this.props.selectedBucket.path) {
      advancedFields = _.map(advancedFields, (field) => {
        // default values of custom paths
        if (field.name === 'whitelist.pathmatchers') {
          return { ...field, default: this.props.selectedBucket.path };
        }

        // disable lower HC scale factors
        if (field.name === 'hc_scale_factor') {
          const hcScaleFactorOptions = UIUtils.disableInvalidHCOptions(
            field.options,
            this.props.sourceBeingEdited &&
              this.props.sourceBeingEdited.source_config &&
              this.props.sourceBeingEdited.source_config['hc_scale_factor'],
          );
          return { ...field, options: hcScaleFactorOptions };
        }

        return field;
      });
    }

    const filePath = UIUtils.getLocationFromSourceConfig(
      this.props.selectedBucket,
      connectionCategory,
    );

    const {
      onInputChange,
      selectedConnection,
      dataSchema,
      sinkConfig,
      sourceConfig,
      sourceName,
      sourceDescription,
      visibleFieldData,
      dataSet,
      isQuarantine,
      addedDataSink,
      addedDataSource,
      sourceBeingEdited,
    } = this.props;

    const filteredAdvancedFields = advancedFields.filter((o) => o.name !== 'start.cron') || [];
    const cronField = advancedFields.find((o) => o.name === 'start.cron');
    const isHCEnabled = UIUtils.isHCEnabled(
      this.props.currentOrg?.id,
      this.props.selectedConnection,
    );

    const runtimeSelectionEnabled =
      !sinkConfig &&
      isSourceFormRuntimeSelectionEnabled(
        getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
        getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
        CONNECTION_CATEGORY_FILE,
        this.props.flowType,
      );
    const runtimeSelectionVisible = this.props.runtimeSelectionVisible !== false;

    const hasRuntimeSelectionInFields = filteredAdvancedFields.some(
      (o) => o.type === 'runtime_selection',
    );
    const runtimeSelectionReadOnly = Boolean(
      this.props.sourceBeingEditedId || sourceBeingEdited?.id,
    );
    const runtimeSelectionSection = runtimeSelectionReadOnly
      ? 'Selected Runtime'
      : 'Select Runtime';
    const runtimeFields = filteredAdvancedFields.map((field) =>
      field.type === 'runtime_selection' ? { ...field, section: runtimeSelectionSection } : field,
    );
    const fieldsWithRuntime =
      runtimeSelectionEnabled && !hasRuntimeSelectionInFields
        ? [
            ...runtimeFields,
            {
              name: 'runtime_selection',
              type: 'runtime_selection',
              section: runtimeSelectionSection,
            },
          ]
        : runtimeFields;

    const fields = _.filter(
      fieldsWithRuntime,
      (o) =>
        !(
          o.name === 'path' ||
          o.name === 'output.dir.name.pattern' ||
          (!isHCEnabled && Constants.HC_SOURCE_FIELD_NAMES.includes(o.name)) ||
          ((!runtimeSelectionEnabled || !runtimeSelectionVisible) && o.type === 'runtime_selection')
        ),
    );

    const sections = UIUtils.getActiveSections(fields, sourceConfig || sinkConfig);

    const isDestination = !!sinkConfig;
    const resourcePersistedForPopover = isDestination
      ? !!addedDataSink?.id
      : !!(sourceBeingEdited?.id || addedDataSource?.id);
    const resourceConfig = this.getResourceConfig();

    const isIDBased = ID_BASED_FILE_CONNECTORS.includes(this.props?.selectedConnection);
    const isConnectorWithLeafIdOnly = ID_BASED_FILE_CONNECTORS_WITH_LEAF_ID_ONLY.includes(
      this.props?.selectedConnection,
    );
    const fileIngestModifiedAfterMs = getFileIngestModifiedAfterMs(this.props.sourceConfig);

    return (
      <div className="select-bucket-container">
        {this.props.hideMetaConfiguration ? null : (
          <DataSourceDetails
            name={sourceName}
            description={sourceDescription}
            onInputChange={onInputChange}
            leftView={
              <HeaderMetaConfiguration
                nexsetId={dataSet?.id}
                enableInputFields={false}
                connection={selectedConnection}
                isDestination={isDestination}
                nexsetChipChildren={
                  <ResourcePopoverMenu
                    nexsetId={dataSet?.id}
                    isDestination={isDestination}
                    resourceConfig={resourceConfig}
                    connectorType={selectedConnection}
                    jsonConfigOnSave={this.onSaveAdvancedMode}
                    onSwitchUiMode={this.onSwitchUiMode}
                    resourcePersisted={resourcePersistedForPopover}
                    jsonConfigOnClose={() => {
                      if (this.props.goBack) {
                        this.props.goBack();
                      }
                    }}
                    disabledActions={['view-json-config']}
                  />
                }
                sourceOrDestinationChipChildren={
                  <ResourcePopoverMenu
                    isDestination={isDestination}
                    resourceConfig={resourceConfig}
                    connectorType={selectedConnection}
                    jsonConfigOnSave={this.onSaveAdvancedMode}
                    onSwitchUiMode={this.onSwitchUiMode}
                    resourcePersisted={resourcePersistedForPopover}
                    jsonConfigOnClose={() => {
                      if (this.props.goBack) {
                        this.props.goBack();
                      }
                    }}
                    disabledActions={['view-nexset-details']}
                  />
                }
              />
            }
          />
        )}
        <SearchableProvider>
          {(onChangeSearch) => (
            <NavScroller
              navClassName="left-nav"
              navOptions={[
                ...(this.state.hidePath
                  ? []
                  : [
                      {
                        label:
                          isDestination || isQuarantine
                            ? 'Destination Directory'
                            : 'Choose Source Folder',
                        id: 'source',
                      },
                    ]),

                ...(!this.props.isReplication && isDestination && !this.state.hidePath
                  ? [
                      {
                        label: 'Define Processing Logic',
                        id: 'sb-data-format',
                      },
                    ]
                  : []),
                ...sections
                  .map((section) => ({
                    label: section,
                    id: UIUtils.getSectionElementId(section),
                  }))
                  .filter((section) => section.label !== 'Apply File Filters'),
                ...(!!cronField
                  ? [
                      {
                        label: isQuarantine ? 'Schedule Writing' : 'Schedule Ingestion',
                        id: 'section-scheduling',
                      },
                    ]
                  : []),
              ]}
              offset={24}
            >
              <NavScrollerContext.Consumer>
                {(consumer) => {
                  return (
                    <div className="nav-scroller-container">
                      {!this.state.hidePath ? (
                        <ResizableHorizontalLayout
                          minSizes={[400, 320]}
                          initialSizes={['75%', '25%']}
                        >
                          <Box
                            className={classnames({
                              'select-bucket-box': true,
                              'left-part': true,
                              'select-bucket-box-focused': consumer?.activeScroll === 'source',
                            })}
                            rightView={
                              <>
                                <span id="source" className="hidden-source-element" />
                                <Stack
                                  direction="row"
                                  flex={1}
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    variant="body3"
                                    className="select-bucket-box-subtitle"
                                  >
                                    {isDestination || isQuarantine
                                      ? 'Select the folder where the system will write files.'
                                      : 'Select the folder where the system will scan and retrieve files for processing.'}
                                  </Typography>
                                </Stack>
                              </>
                            }
                            title={
                              isDestination || isQuarantine
                                ? 'Destination Directory'
                                : 'Choose Source Folder'
                            }
                          >
                            <FileFilters
                              selectedNavScrollerSection={consumer?.activeScroll}
                              sourceConfig={this.props.sourceConfig}
                              onSourceConfigChanged={this.props.onSourceConfigChanged}
                              sourceConfigFields={this.props.sourceConfigFields}
                              selectedBucket={this.props.selectedBucket}
                            />

                            <div className="select-bucket-tree-list">
                              {this.props.probeError ? (
                                <Stack padding="40px">
                                  <EmptyView
                                    title="There was an error loading resources"
                                    actionTitle="Try Again"
                                    actionProps={{
                                      onClick: this.onRequestTree,
                                    }}
                                  />
                                </Stack>
                              ) : (
                                <>
                                  {/* Show loading spinner as overlay when loading initial data */}
                                  {isLoading && (
                                    <div
                                      className="select-bucket-tree-list-loading"
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.8)',
                                        zIndex: 1,
                                      }}
                                    >
                                      <CircularProgress />
                                      <span style={{ marginLeft: 'var(--spacing-md)' }}>
                                        Connecting to {meta?.displayName}...
                                      </span>
                                    </div>
                                  )}
                                  {/* Always render BreadcrumbTreeView to prevent remounting */}
                                  <SearchInjector key="search-injector-stable">
                                    <BreadcrumbTreeView
                                      key="breadcrumb-tree-view-stable"
                                      data={this.props.probeData}
                                      fileIngestModifiedAfterMs={fileIngestModifiedAfterMs}
                                      selectedNodeId={this.state.selectedNodeId}
                                      lastUpdatedAt={this.props.probeTreeLastUpdatedAt}
                                      selectable={true}
                                      enableFileSelection={this.props.enableFileSelection}
                                      selectableFileMimeType={this.props.selectableFileMimeType}
                                      nodeKeyName="children"
                                      iconClasses={iconClasses}
                                      isLoadingData={this.props.isLoadingProbeData}
                                      onNodePreview={this._handleNodePreview}
                                      onNodeSelected={this._handleNodeSelected}
                                      onNodeExpandClicked={this._handleNodeExpandClicked}
                                      stopAtParent={!!this.props.sourceBeingEdited || isDestination}
                                      onSearchChange={onChangeSearch}
                                      onPathInputChange={this._handleNodeSelected}
                                      pathInputValue={filePath}
                                      pathInputReadOnly={isIDBased && this.props.probeData}
                                      pathInputPlaceholder={
                                        isIDBased && !this.props.probeData
                                          ? 'Type the path ID here'
                                          : undefined
                                      }
                                      isLoadingProbeData={this.props.isLoadingProbeData}
                                      onRefreshTreeClicked={this._handleRefreshTreeClicked}
                                      isDestination={isDestination}
                                      allPrefixSuggestions={this.state.allPrefixSuggestions}
                                      customPrefixEditorState={this.state.customPrefixEditorState}
                                      filteredPrefixSuggestions={
                                        this.state.filteredPrefixSuggestions
                                      }
                                      handlePrefixEditorChange={this._handlePrefixEditorChange}
                                      handlePrefixEditorSearch={this._handlePrefixEditorSearch}
                                      handlePrefixEditorKeyDown={this._handlePrefixEditorKeyDown}
                                      stateUpdatedAt={this.state.stateUpdatedAt}
                                      uiDisplayPath={this.props.sourceConfig?.['ui.display_path']}
                                      uiIdPath={this.props.sourceConfig?.['ui.id_path']}
                                      isIDBased={isIDBased}
                                      isConnectorWithLeafIdOnly={isConnectorWithLeafIdOnly}
                                      onIdPathResolved={this._handleIdPathResolved}
                                    />
                                  </SearchInjector>
                                </>
                              )}
                            </div>
                          </Box>

                          <div className="right-part">
                            {Boolean(this.state.fileNode) ? (
                              <FilePreview
                                selectedConnection={this.props.selectedConnection}
                                selectedCredentialId={this.props.selectedCredentialId}
                                onClose={this.closeDialog}
                                fileNode={this.state.fileNode}
                                height="40vh"
                              />
                            ) : (
                              <>
                                <Typography variant="body2" className="file-preview-title">
                                  File Preview{' '}
                                </Typography>
                                <Typography
                                  variant="bodySmall"
                                  className="file-preview-description"
                                >
                                  Preview data will appear here
                                </Typography>
                              </>
                            )}
                          </div>
                        </ResizableHorizontalLayout>
                      ) : null}

                      {/* BYO destinations write whatever the user's own Ray custom-code
                          function returns — the Ray executor's BYOPayload has no field
                          for a custom header row, a file-name prefix, or a max-file-size
                          split (see nexla-ray/ray-flows-payloads-and-config.md §B.2 and
                          file_handler.py/byo_opt_processor.py, neither of which read any
                          such config). Showing these controls for a BYO sink is a dead
                          end — nothing on the backend ever applies them. */}
                      {isDestination &&
                      !this.state.hidePath &&
                      this.props.flowType !== FLOW_TYPE_CUSTOM ? (
                        <div id="sb-data-format">
                          <FormatCardContainer
                            {...this.props}
                            containerClassName={classnames({
                              'box-focused': consumer?.activeScroll === 'sb-data-format',
                            })}
                            dataSchema={this._getSinkMappingAttributes(
                              selectedConnection,
                              dataSchema,
                              sinkConfig,
                            )}
                          />
                        </div>
                      ) : null}

                      <div id="section-heading-data-format">
                        <AdvancedConfiguration
                          selectedNavScrollerSection={consumer?.activeScroll}
                          selectedConnection={this.props.selectedConnection}
                          sourceConfigFields={fields}
                          sourceConfig={this.props.sourceConfig}
                          onSourceConfigChanged={this.props.onSourceConfigChanged}
                          resourceMode={this.props.resourceMode}
                          connectionType={this.props.selectedConnection}
                          connectionCategory={connectionCategory}
                          renderSectionFields={this.props.renderSectionFields}
                        />
                      </div>

                      {/* Only mount once cronField (from connector metadata) has
                          loaded — MonitoringInterval initializes state from
                          field.name, so mounting with an undefined field leaves it
                          stale and violates the Rules of Hooks (see the guard in
                          MonitoringInterval). */}
                      {!!cronField && (
                        <div id="section-scheduling">
                          <MonitoringInterval
                            field={cronField}
                            source={this.props.sourceBeingEdited}
                            config={visibleFieldData}
                            showFlowTriggers={this.props.showFlowTriggers}
                            monitorIntervalContainerClassname={classnames({
                              scheduling: true,
                              'scheduling-selected':
                                consumer?.activeScroll === 'section-scheduling',
                            })}
                            onConfigChanged={this.props.onSourceConfigChanged}
                          />
                        </div>
                      )}
                    </div>
                  );
                }}
              </NavScrollerContext.Consumer>
            </NavScroller>
          )}
        </SearchableProvider>
      </div>
    );
  }

  onSaveAdvancedMode = async (configString) => {
    const {
      sinkConfig,
      sourceConfig,
      nextFormStage,
      onError = (msg = '') => {},
      onSaveAdvancedMode: saveAdvancedMode,
    } = this.props;

    if (!JSONUtils.isJSON(configString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const parsedConfig = JSON.parse(configString);

    let data = {};

    // NEX-21470: in code mode the user's paste is authoritative — do not
    // re-inject selectedBucket.path on top of what they sent.
    if (sinkConfig) {
      data = { ...parsedConfig['sink_config'] };
    }

    if (sourceConfig) {
      data = { ...data, ...parsedConfig['source_config'] };
    }

    data = {
      ...data,
      name: parsedConfig?.name,
      description: parsedConfig?.description,
    };

    const dataWithMode = withUiModeCode(data);
    const { error } = await tryCatchAsync(saveAdvancedMode(dataWithMode));

    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, sourceConfig, onSinkConfigChanged, onSourceConfigChanged } = this.props;

    if (sinkConfig && typeof onSinkConfigChanged === 'function') {
      onSinkConfigChanged({ ...sinkConfig, 'ui.mode': UiMode.VIEW });
    }

    if (sourceConfig && typeof onSourceConfigChanged === 'function') {
      onSourceConfigChanged({ ...sourceConfig, 'ui.mode': UiMode.VIEW });
    }
  };

  getResourceConfig = () => {
    const { data, sinkConfig, sourceConfig } = this.props;
    if (sinkConfig) {
      const clonedSinkConfig = structuredClone(sinkConfig);
      const name = data?.name || clonedSinkConfig?.name || clonedSinkConfig?.sinkName || '';
      const description =
        data?.description ||
        clonedSinkConfig?.description ||
        clonedSinkConfig?.sourceDescription ||
        '';

      delete clonedSinkConfig.name;
      delete clonedSinkConfig.sourceName;
      delete clonedSinkConfig.description;
      delete clonedSinkConfig.sourceDescription;
      return {
        name,
        description,
        sink_config: clonedSinkConfig,
      };
    }

    if (sourceConfig) {
      const { sourceName, sourceDescription } = this.props;
      const clonedSourceConfig = structuredClone(sourceConfig);

      const name =
        data?.name ||
        sourceName ||
        clonedSourceConfig?.name ||
        clonedSourceConfig?.sourceName ||
        '';

      const description =
        data?.description ||
        sourceDescription ||
        clonedSourceConfig?.description ||
        clonedSourceConfig?.sourceDescription ||
        '';

      delete clonedSourceConfig.name;
      delete clonedSourceConfig.sourceName;
      delete clonedSourceConfig.description;
      delete clonedSourceConfig.sourceDescription;

      return {
        name,
        description,
        source_config: clonedSourceConfig,
      };
    }

    return {};
  };

  _getSinkMappingAttributes = (selectedConnection, allAttributes, sinkConfig) => {
    const {
      paging: { metaInfoById },
    } = this.props;
    const { connectionCategory } = metaInfoById[selectedConnection] || {};

    if (
      connectionCategory === CONNECTION_CATEGORY_DATABASE ||
      (connectionCategory === CONNECTION_CATEGORY_FILE &&
        UIUtils.formatsWithColumns.indexOf(sinkConfig && sinkConfig.data_format) > -1)
    ) {
      // exclude attributes controlled by arrays
      const attributesByName = groupBy(allAttributes, '__name');
      return filter(
        allAttributes,
        ({ __controlledBy }) =>
          !attributesByName[__controlledBy] ||
          attributesByName[__controlledBy][0].__type !== 'array',
      );
    }

    return allAttributes;
  };

  _updateSourceConfigPrefix() {
    const { sourceConfig, onSourceConfigChanged } = this.props;
    const { customPrefixEditorState } = this.state;
    const customPrefixPart = this._saveCustomPrefix(customPrefixEditorState);

    onSourceConfigChanged({
      ...sourceConfig,
      'output.dir.name.pattern': customPrefixPart,
    });
  }

  _getPathKey(node) {
    // Normalize path key to ensure consistent tracking
    // Use path if available, otherwise use id, fallback to empty string
    const path = node?.path || node?.id || '';
    // Normalize: remove trailing slashes and ensure consistent format
    return path.replace(/\/+$/, '') || '';
  }

  _handleNodeExpandClicked(selectedNode, forceProbe = false) {
    if (selectedNode.type === 'folder') {
      const pathKey = this._getPathKey(selectedNode);

      // Skip limit check for root probes (empty path or just '/')
      // Root probes are for credential validation and shouldn't count against path limits
      const isRootProbe = !pathKey || pathKey === '/' || pathKey === '';

      // When forceProbe is true (e.g., breadcrumb navigation, folder double-click, path entry),
      // ALWAYS make API call to get fresh data - skip the limit check
      // This ensures explicit user actions always get fresh data
      if (forceProbe) {
        this.props.onRequestProbeTree(false, selectedNode);
        return;
      }

      // For automatic probes (not user-initiated), apply the limit check
      if (!isRootProbe) {
        // Check probe call count - hard limit of 2 calls per path
        const currentCount = this.probeCallCountMap.get(pathKey) || 0;

        if (currentCount >= this.maxProbeCalls) {
          // Already made 2 calls for this path - stop to prevent infinite loops
          console.warn(
            `[SelectBucket] Probe call limit (${this.maxProbeCalls}) reached for path: ${pathKey}. Stopping to prevent infinite loop.`,
          );
          return;
        }
      }

      if (selectedNode.scanned || (selectedNode.children && !_.isEmpty(selectedNode.children))) {
        // Node already scanned or has children - just toggle, no probe needed
        this.props.onToggleNodeProbeTree(selectedNode);
      } else {
        // Increment probe count before making call (only for non-root paths)
        if (!isRootProbe) {
          const currentCount = this.probeCallCountMap.get(pathKey) || 0;
          this.probeCallCountMap.set(pathKey, currentCount + 1);
        }
        this.props.onRequestProbeTree(false, selectedNode);
      }
    }
  }

  _handleNodePreview(selectedNode, selectedNodeId) {
    this.setState({
      fileNode: selectedNode,
    });
  }

  _handleNodeSelected(selectedNode, selectedNodeId) {
    // If explicitly passing null, clear selection
    if (!selectedNode) {
      this.setState({ selectedNodeId: null }, () => {
        this.props.onSelected(null);
      });
      return;
    }

    // If node has path data (from initial load or navigation), always set it
    // Don't treat as toggle - we want to confirm the selection with full path info
    if (selectedNode.path || selectedNode.displayPath) {
      // DEBUG: Uncomment to debug selection updates
      // console.log('[SelectBucket] _handleNodeSelected - Setting selection:', {
      //   selectedNodeId,
      //   path: selectedNode.path,
      //   displayPath: selectedNode.displayPath
      // });
      this.setState({ selectedNodeId }, () => {
        this.props.onSelected(selectedNode);
      });
      return;
    }

    // Toggle behavior for clicks without path data (legacy case)
    if (selectedNode.id === this.state.selectedNodeId) {
      this.setState({ selectedNodeId: null }, () => {
        this.props.onSelected(null);
      });
    } else {
      this.setState({ selectedNodeId }, () => {
        this.props.onSelected(selectedNode);
      });
    }
  }

  _handleRefreshTreeClicked(target) {
    const t = target || {};
    const node = t.node;
    const path = t.path;
    const depth = t.depth;
    if (node) {
      this.props.onRequestProbeTree(false, node);
      return;
    }
    if (path) {
      const lastSeg = String(path).split('/').filter(Boolean).pop() || String(path);
      const pseudoNode = {
        id: path,
        name: lastSeg,
        path,
        type: 'folder',
      };
      this.props.onRequestProbeTree(false, pseudoNode);
      return;
    }
    this.props.onRequestProbeTree();
  }

  /**
   * Handle resolved ID path for leaf-ID-only connectors (Google Drive)
   * This is called when the full ID path has been resolved from the display path.
   * We need to update the source config to include ui.id_path so future edits don't need to re-resolve.
   */
  _handleIdPathResolved(idPath) {
    console.log('[SelectBucket] ID path resolved:', idPath);

    // Update source config to include the resolved ID path
    // This will be saved when the user saves the source/sink
    const updatedConfig = {
      ...this.props.sourceConfig,
      'ui.id_path': idPath,
    };

    // Notify parent to update the source config
    // Note: We don't have a direct way to update just the config without updating the selected node
    // So we create a pseudo-node that represents the current selection with the ID path
    if (this.state.selectedNodeId) {
      const pseudoNode = {
        id: this.state.selectedNodeId,
        path: this.state.selectedNodeId, // Keep the leaf ID as path (for backward compatibility)
        displayPath: this.props.sourceConfig?.['ui.display_path'],
        idPath: idPath, // Add the resolved ID path
        type: 'folder',
      };

      // Trigger selection update with the ID path info
      // The parent will merge this into sourceConfig
      this.props.onSelected(pseudoNode);
    }
  }

  _saveCustomPrefix = (editorState) => {
    const { allPrefixSuggestions } = this.state;
    const contentState = editorState.getCurrentContent();
    let { payloadText } = extractPayloadFromMentionContent(
      contentState,
      DEFAULT_MACRO_ENTITY_NAME,
      true,
      allPrefixSuggestions,
      replaceMacroMap,
    );

    return payloadText;
  };

  _handlePrefixEditorKeyDown(event, editorState) {
    // if (event.keyCode === 13) {
    //   // this.props.onCallAPI();
    // }
    const { sourceConfig } = this.props;
    let customPrefixPart = this._saveCustomPrefix(editorState);

    let newSourceConfig = {
      ...sourceConfig,
      'output.dir.name.pattern': customPrefixPart,
    };

    this.props.onSourceConfigChanged(newSourceConfig);
  }

  _handlePrefixEditorChange(editorState) {
    if (
      editorState.getCurrentContent() !== this.state.customPrefixEditorState.getCurrentContent()
    ) {
      // content has changed
      this._saveCustomPrefixDebounced(editorState);
    }

    this.setState({
      customPrefixEditorState: editorState,
    });
  }

  _saveCustomPrefixDebounced = _.debounce((editorState) => {
    const { sourceConfig } = this.props;
    let customPrefixPart = this._saveCustomPrefix(editorState);
    let newSourceConfig = {
      ...sourceConfig,
      'output.dir.name.pattern': customPrefixPart,
    };
    this.props.onSourceConfigChanged(newSourceConfig);
  }, 1000);

  _handlePrefixEditorSearch(suggestions) {
    this.setState({
      filteredPrefixSuggestions: suggestions,
    });
  }
}

SelectBucket.defaultProps = {
  isLoadingProbeData: true,
  allowCustomPrefixEditor: false,
  shouldShowLoading: true,
  resourceMode: 'source',
  isModal: false,
};

const mapStateToProps = (state) => {
  return {
    currentOrg: getCurrentOrg(state),
  };
};

export default connect(mapStateToProps)(SelectBucket);
